import { createContext, useContext, useEffect, useLayoutEffect, useRef } from "react";
import type { ReactElement, ReactNode } from "react";
import { Abschnitt } from "@kundeninfo/ui/me-design";
import type { Objektart } from "@kundeninfo/ui/me-design";
import type { Kopffaltung } from "./Kopffaltung.js";
import { keepInView } from "../scroll.js";
import { SECTION_TEXT } from "./vorgangsabschnitt/texts.js";
import "./Vorgangsabschnitt.css";

/**
 * Der VORGANGSABSCHNITT (Spec „Abschnittsmuster der Vorgangsdialoge“): ein `me-abschnitt` mit drei Zusagen.
 *
 *  - **Objektart.** `art` nennt die Objektart des Inhalts. Plakette, Kantenband und Zusammenfassung: me-design 73d07f0,
 *    Entscheide 93/96 (MW1/MW5) — das Element zeichnet die getönte Plakette vor dem Titel und das Band (Part `traeger`,
 *    am Telefon oben) selbst, als Kennzeichen; anklickbar bleibt allein der Klappknopf (Hausregel „Rahmen heißt
 *    anklickbar, Fläche heißt Kennzeichen“, Entscheid 473). Gleiche Inhalte tragen in jedem Vorgang dieselbe Farbe.
 *    `art: null` (Vorlage, Verwaltung) zeigt weder Plakette noch Band.
 *  - **Aussage im zugeklappten Zustand.** Zähler, Fehlerzahl (auch zugeklappt in Fehlerfarbe) und die leise
 *    Zusammenfassung rechts im Kopf, die das Element offen selbst ausblendet.
 *
 * Die Mechanik der früheren Kopfblöcke und Aufklapper bleibt: Der Zustand gehört der MASKE (`useKopffaltung`, je Abschnitt
 * `useSectionFolds`), das Element schaltet beim Klick selbst um und die Hülle zieht es auf den Prop zurück; eingeklappt heißt
 * versteckt, nicht abgeräumt (der Träger im Slot bleibt derselbe Knoten, Fokus und Eingaben überleben das Zuklappen).
 *
 * Auf- und Zuklappen hält den geklickten Kopf an seiner Stelle (Hausregel „Scroll bei Neuzeichnen“): Die Lage wird VOR dem
 * Umschalten gemerkt und nach dem Zeichnen ausgeglichen (`keepInView`). „Vor“ heißt: in einem Klick-Zuhörer im
 * Capture-Modus am Wirt. me-abschnitt setzt `offen` und das `hidden` seines Rahmens schon im Klick auf seinen Knopf, VOR
 * `me-umgeschaltet` — dort gemessen sähe die Messung beim Zuklappen schon den kürzeren Inhalt (und eine vom Browser
 * geklemmte Rollposition) und glich nichts aus. Der Capture-Zuhörer am Wirt läuft vor dem Knopf im Shadow DOM.
 *
 * Ein Baustein im Abschnitt (Parteien, Zahlplan, Anlagen) wiederholt dessen Titel nicht: Er fragt `useInSection()`.
 */
export type VorgangsabschnittProps = {
  /** Objektart des Inhalts; `null` = ohne Plakette und Band. */
  art: Objektart | null;
  titel: string;
  faltung: Kopffaltung;
  /** Die Kennung des eingefalteten Inhalts (der Träger im Slot von `me-abschnitt`). */
  inhaltId: string;
  /** Zahl in der Pille (Positionen, Dateien, Raten …). */
  zaehler?: number;
  /** Zahl der Felder mit Fehler im Abschnitt; größer 0 ersetzt die Pille durch die Fehlerzahl. */
  fehler?: number;
  /** Leiser Kurztext im Kopf, nur zugeklappt sichtbar (Betrag, erste Textzeile, „leer“). */
  zusammenfassung?: string;
  /** Eigene Bedienelemente rechts im Kopf, neben dem Klappknopf. */
  aktionen?: ReactNode;
  children: ReactNode;
};

/** Steht der Baustein in einem Vorgangsabschnitt? Dann trägt der Abschnitt den Titel, der Baustein zeigt keinen eigenen. */
const InSection = createContext(false);
export const useInSection = (): boolean => useContext(InSection);

export function Vorgangsabschnitt(p: VorgangsabschnittProps): ReactElement {
  const { faltung } = p;
  const kopf = useRef<HTMLElement | null>(null);
  const ausgleich = useRef<(() => void) | null>(null);
  useLayoutEffect(() => { ausgleich.current?.(); ausgleich.current = null; });
  useEffect(() => {
    const host = kopf.current;
    if (host === null) return undefined;
    // Nur der Klappknopf misst — ein Klick in den Inhalt oder in die Aktionen läuft ebenfalls durch den Wirt.
    const measure = (e: Event): void => {
      const knopf = host.shadowRoot?.querySelector(".ueberschrift > button.knopf");
      if (knopf != null && e.composedPath().includes(knopf)) ausgleich.current = keepInView(host);
    };
    host.addEventListener("click", measure, true);
    return () => { host.removeEventListener("click", measure, true); };
  }, []);
  return (
    <Abschnitt ref={kopf} className="vabs" titel={p.titel} offen={faltung.offen}
      {...(p.art === null ? {} : { art: p.art })}
      {...(p.zusammenfassung === undefined ? {} : { zusammenfassung: p.zusammenfassung })}
      {...(p.zaehler === undefined ? {} : { zaehler: p.zaehler })}
      {...(p.fehler !== undefined && p.fehler > 0 ? { fehler: p.fehler } : {})}
      aktionen={p.aktionen}
      onUmgeschaltet={(d) => {
        if (d.offen === faltung.offen) { ausgleich.current = null; return; }
        faltung.umschalten();
      }}>
      {/* `hidden` auch am Träger im Light DOM: Das Element versteckt seinen Rahmen im Shadow DOM; der Träger selbst soll
          ebenso aus dem Zugänglichkeitsbaum sein. */}
      <div id={p.inhaltId} className="vabs__inhalt" hidden={!faltung.offen}>
        <InSection.Provider value>{p.children}</InSection.Provider>
      </div>
    </Abschnitt>
  );
}

/** Markdown-Zeichen am Zeilenanfang: Zitat, Überschrift, Aufzählung (diese nur mit folgendem Leerraum — „-5 %“ bleibt). */
const MARKDOWN_VORSPANN = /^(?:\s*(?:>|#{1,6}(?=\s|$)|[-*](?=\s|$)))*\s*/;
const ZUSAMMENFASSUNG_MAX = 80;

/**
 * Die Zusammenfassung eines zugeklappten Textabschnitts: die erste nicht leere Zeile ohne Markdown-Vorspann, gekürzt auf
 * 80 Zeichen mit „…“, sonst „leer“.
 */
export function ersteZeile(text: string | null): string {
  const zeile = (text ?? "").split("\n").map((z) => z.replace(MARKDOWN_VORSPANN, "").trim()).find((z) => z !== "");
  if (zeile === undefined) return SECTION_TEXT.empty;
  return zeile.length > ZUSAMMENFASSUNG_MAX ? `${zeile.slice(0, ZUSAMMENFASSUNG_MAX)}…` : zeile;
}
